import React from 'react';
import { Property } from '../types/property.types';

interface PropertySummaryProps {
  property: Property;
  showOwnerData?: boolean;
}

const PropertySummary: React.FC<PropertySummaryProps> = ({ 
  property, 
  showOwnerData = false 
}) => {
  const formatCurrency = (value?: number) => {
    if (!value) return 'No especificado';
    return `$${value.toLocaleString()}`;
  };

  const formatArea = (area?: number) => {
    if (!area) return null;
    return `${area}m²`;
  };

  return (
    <div className="space-y-4 text-sm">
      {/* Información básica */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <span className="font-medium text-gray-700">🏠 Tipo:</span>
          <span className="ml-2 text-gray-900">{property.tipo_inmueble}</span>
        </div>
        <div>
          <span className="font-medium text-gray-700">📋 Transacción:</span>
          <span className="ml-2 text-gray-900">{property.transaccion}</span>
        </div>
        <div>
          <span className="font-medium text-gray-700">📍 Ciudad:</span>
          <span className="ml-2 text-gray-900">{property.ciudad}</span>
        </div>
        <div>
          <span className="font-medium text-gray-700">🏘️ Sector:</span>
          <span className="ml-2 text-gray-900">{property.sector}</span>
        </div>
      </div>

      {/* Dirección completa */}
      <div>
        <span className="font-medium text-gray-700">📍 Dirección:</span>
        <span className="ml-2 text-gray-900">{property.direccion}</span>
        {property.edificio && (
          <span className="ml-2 text-gray-600">({property.edificio})</span>
        )}
      </div>

      {/* Características físicas */}
      <div>
        <h4 className="font-medium text-gray-700 mb-2">🏗️ Características</h4>
        <div className="grid grid-cols-3 gap-3 text-sm">
          {property.habitaciones !== undefined && (
            <div>🛏️ {property.habitaciones} hab</div>
          )}
          {property.banos !== undefined && (
            <div>🚿 {property.banos} baños</div>
          )}
          <div>🚗 {property.parqueaderos} parq</div>
          
          {property.estrato && (
            <div>📊 Estrato {property.estrato}</div>
          )}
          {property.niveles && property.niveles > 1 && (
            <div>🏢 {property.niveles} niveles</div>
          )}
          {property.area_construida && (
            <div>📐 {formatArea(property.area_construida)}</div>
          )}
          
          {property.piscina && <div>🏊‍♂️ Piscina</div>}
          {property.ascensor && <div>🛗 Ascensor</div>}
          {property.balcon && <div>🌿 Balcón</div>}
        </div>
      </div>

      {/* Información comercial */}
      <div>
        <h4 className="font-medium text-gray-700 mb-2">💰 Información Comercial</h4>
        <div className="space-y-1">
          {property.valor_arriendo && (
            <div>
              <span className="font-medium">Arriendo:</span>
              <span className="ml-2 text-green-600 font-bold">{formatCurrency(property.valor_arriendo)}</span>
            </div>
          )}
          {property.valor_venta && (
            <div>
              <span className="font-medium">Venta:</span>
              <span className="ml-2 text-blue-600 font-bold">{formatCurrency(property.valor_venta)}</span>
            </div>
          )}
          {property.administracion && (
            <div>
              <span className="font-medium">Administración:</span>
              <span className="ml-2">{formatCurrency(property.administracion)}</span>
            </div>
          )}
          {property.predial && (
            <div>
              <span className="font-medium">Predial:</span>
              <span className="ml-2">{formatCurrency(property.predial)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Información del propietario */}
      {showOwnerData && (property.nombre_propietario || property.telefono_propietario || property.email_propietario) && (
        <div>
          <h4 className="font-medium text-gray-700 mb-2">👤 Información del Propietario</h4>
          <div className="space-y-1">
            {property.nombre_propietario && (
              <div>
                <span className="font-medium">Nombre:</span>
                <span className="ml-2">{property.nombre_propietario}</span>
              </div>
            )}
            {property.telefono_propietario && (
              <div>
                <span className="font-medium">Teléfono:</span>
                <span className="ml-2">{property.telefono_propietario}</span>
              </div>
            )}
            {property.email_propietario && (
              <div>
                <span className="font-medium">Email:</span>
                <span className="ml-2">{property.email_propietario}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Servicios y amenidades */}
      {(property.servicios || property.zonas_comunes) && (
        <div>
          <h4 className="font-medium text-gray-700 mb-2">🏨 Servicios y Amenidades</h4>
          <div className="space-y-1">
            {property.servicios && (
              <div>
                <span className="font-medium">Servicios:</span>
                <span className="ml-2">{property.servicios}</span>
              </div>
            )}
            {property.zonas_comunes && (
              <div>
                <span className="font-medium">Zonas comunes:</span>
                <span className="ml-2">{property.zonas_comunes}</span>
              </div>
            )}
            {property.llaves && (
              <div>
                <span className="font-medium">Llaves:</span>
                <span className="ml-2">{property.llaves}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Observaciones */}
      {property.observaciones && (
        <div>
          <h4 className="font-medium text-gray-700 mb-2">📝 Observaciones</h4>
          <div className="bg-white p-3 rounded border text-gray-700">
            {property.observaciones}
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertySummary;
